import { Dispatch, createAsyncThunk } from '@reduxjs/toolkit';
import {
  DatasourcesActions,
  DesignerRequestVerb,
  FileFolderType,
  FolderRootType,
  IFileInfo,
  IFileInfoResponse,
  IGetFileInfo,
  IMultilineMessage,
  INodeInfo,
  ITab,
  ITabExtra,
  IWebFormContent,
  OrNull,
  ROLES_TAB_PATH,
  SETTINGS_TAB_PATH,
  WebformActions,
  by,
  datasourcesSubject,
  getFileExtentionFromType,
  getFilesInfo,
  getFolders,
  getNodeInfo,
  isObjectDatasource,
  isSimpleDatasource,
  // addAttributesToContent,
  parseAttributes,
  saveContent,
  webformSubject,
  ModalColor,
  ModalType,
  WebformState,
} from '@qodly/shared';
import { getExtension } from '~store/modules/explorer/utils';
import { modelSubject } from '~store/modules/model/subjects';
import {
  CATALOG_REFRESH_DELAY,
  deserializeModel,
  getModalTitle,
} from '~store/utils';
import { AppState, fetchCatalog } from '..';
import { selectTabs, selectTouchedTabs } from '../../selectors';
import { createModelFiles } from '../explorer/thunks';
import { openModal } from '../modals/thunks';
import { addUUIDToModel } from '../model/utils';
import { setRolesContent, updateRolesAndPrivileges } from '../roles';
import { sanitize, toAPI } from '../roles/adapter';
import { fetchRoles } from '../roles/thunks';
import { setSettings } from '../settings';
import {
  closeAllTabs,
  closeLeftTabs,
  closeOthers,
  closeRightTabs,
  closeTab,
  selectTabByReference,
  setContent,
  setHistoryContent,
  setTabFlags,
  setWebformState,
  unlockHistory,
} from './reducer';
import { ClassFileTypes, TActionRenameDataSource } from './types';
import { debounce } from 'lodash';

export const fetchFiles = createAsyncThunk('files/fetchFiles', async () => {
  return getFolders(
    FolderRootType.WEBFORMS,
    FolderRootType.METHODS,
    FolderRootType.CLASSES,
  );
});

/**
 * handles the rename of a datasource.
 *
 * - if the datasource is a local datasource, it will rename it along with it's references in component and other datasources.
 * - if the datasource is a shared datasource, it will do the same as above but only for current webform.
 */
export const renameDataSource = createAsyncThunk(
  'tabs/renameDatasource',
  async (payload: TActionRenameDataSource, { dispatch, getState }) => {
    const is_shared = !!payload.datasourceNamespace;
    const { confirmed } = await dispatch(
      openModal({
        message: 'Are you sure you want to rename the datasource?',
        title: 'Rename Datasource',
        type: ModalType.CONFIRM,
        color: ModalColor.WARNING,
        views: {
          toggles: {},
          tips: !is_shared
            ? [
                {
                  type: 'warning',
                  message:
                    'The datasource property of components bound with this datasource will also be updated',
                },
                {
                  type: 'warning',
                  message:
                    'The origin of some other datasources might be also renamed (if this datasource is the origin of other datasources)',
                },
              ]
            : [
                {
                  type: 'warning',
                  message:
                    'The datasource property of components bound with this datasource will also be updated on opened web forms only',
                },
                {
                  type: 'warning',
                  message:
                    'The origin of some other datasources might be also renamed (if this datasource is the origin of other datasources)',
                },
              ],
        },
      }),
    ).unwrap();

    if (!confirmed) return null;

    const {
      autoUpdateReferences,
      datasourceId,
      datasourceNamespace,
      value,
      path,
    } = payload;

    const state = getState() as AppState;
    let datasources: datasources.ICreateDataSource[] | undefined = [];
    if (is_shared) {
      const namespace =
        state.sharedDatasources.sources[datasourceNamespace] || [];
      datasources.push(...namespace);
    } else if (path) {
      const webform = state.root.tabs.find(
        by('path', path),
      ) as ITab<IWebFormContent>;

      datasources = webform?.content?.metadata?.datasources;

      if (!datasources) return null;
    }
    const regex = new RegExp(`^${datasourceId}\\b`, '');
    datasources = datasources.map((ds) => {
      const hasParent =
        (isObjectDatasource(ds) || !isSimpleDatasource(ds)) &&
        ds.from &&
        regex.test(ds.from);

      const events = ds.events
        ? ds.events.map((e: webforms.WEvent) => {
            switch (e?.type) {
              case 'method': {
                const event = e as webforms.MemberFunctionEvent;
                return event.params
                  ? {
                      ...event,
                      params: event.params.map((param) => ({
                        ...param,
                        datasource: param.datasource?.replace(
                          new RegExp(`^${datasourceId}`, ''),
                          value,
                        ),
                      })),
                      returns: event.returns?.datasource
                        ? {
                            ...event.returns,
                            datasource: event.returns.datasource?.replace(
                              new RegExp(`^${datasourceId}`, ''),
                              value,
                            ),
                          }
                        : event.returns,
                    }
                  : event;
              }
              case 'simple-action': {
                const event = e as webforms.SimpleActionEvent;
                return event.datasource
                  ? {
                      ...event,
                      datasource: {
                        ...event.datasource,
                        name: event.datasource.name?.replace(
                          new RegExp(`^${datasourceId}`, ''),
                          value,
                        ),
                        from: event.datasource.from?.replace(
                          new RegExp(`^${datasourceId}`, ''),
                          value,
                        ),
                      },
                    }
                  : event;
              }
              default:
                return e;
            }
          })
        : ds.events;

      if (ds.id === datasourceId) {
        return {
          ...ds,
          id: value,
          events,
        };
      } else if (hasParent && ds.from) {
        return {
          ...ds,
          from: ds.from.replace(new RegExp(`^${datasourceId}`, ''), value),
          events,
        };
      }
      return {
        ...ds,
        events,
      };
    });

    if (autoUpdateReferences) {
      // update references in the components
      const old_reference = is_shared
        ? `${datasourceNamespace}:${datasourceId}`
        : `${datasourceId}`;
      const new_reference = is_shared
        ? `${datasourceNamespace}:${value}`
        : `${value}`;
      datasourcesSubject.next({
        path: path ?? '',
        action: DatasourcesActions.RENAME,
        old_reference,
        new_reference,
        is_shared,
      });
    }
    return { path, namespace: datasourceNamespace, datasources };
  },
);

export const editDataSource = createAsyncThunk(
  'tabs/edit-datasource',
  (
    payload: {
      path: string;
      datasourceId: string;
      namespace?: string;
      propertyKey: string;
      value: any;
    },
    { getState },
  ) => {
    // get the datasource
    const state = getState() as AppState;
    let ds_array: datasources.ICreateDataSource[] = [];

    if (payload.namespace) {
      ds_array = state.sharedDatasources.sources[payload.namespace];
    } else {
      const found = state.root.tabs.find(
        by('path', payload.path),
      ) as ITab<IWebFormContent>;

      if (found) {
        ds_array = found.content?.metadata?.datasources || [];
      }
    }

    // edit the datasource property

    const result = ds_array.map((datasource) => {
      if (datasource.id === payload.datasourceId) {
        return Object.assign({}, datasource, {
          [payload.propertyKey]: payload.value,
        });
      }
      return datasource;
    });

    // return updated array of datasource
    return result;
  },
);

export const fetchChanges = createAsyncThunk<
  IFileInfoResponse,
  void,
  { state: AppState }
>('tabs/fetchChanges', async (_f, { getState }) => {
  const openedTabs = selectTabs(getState());

  const codeFiles = openedTabs.filter((tab) =>
    [FileFolderType.METHOD, FileFolderType.CLASS].includes(tab.type),
  );

  return getFilesInfo(
    ...codeFiles.map<IGetFileInfo>((f) => {
      const [root] = f.path.split('/').filter(Boolean);
      return {
        name: f.name,
        type: f.type,
        root: root as FolderRootType,
        path: '.',
      };
    }),
  );
});

export const saveAll = createAsyncThunk<void, void, { state: AppState }>(
  'files/saveAll',
  async (_, { getState, dispatch }) => {
    const touched = selectTouchedTabs(getState());
    for (const tab of touched) {
      dispatch(saveTab({ tab }));
    }
  },
);

export const tryCloseTab = createAsyncThunk<
  boolean,
  string,
  { state: AppState }
>('files/tryCloseTab', async (path, { getState, dispatch }) => {
  const { tabs } = getState().root;

  const index = tabs.findIndex((tab) => tab.path === path);

  if (index === -1) return false;

  const tab = tabs[index];

  if (tab?.flags.touched) {
    const { confirmed } = await dispatch(
      openModal({
        title: getModalTitle(tab),
        message: 'Your unsaved changes will be lost. Proceed anyway?',
        type: ModalType.CONFIRM,
        color: ModalColor.WARNING,
        icon: 'FdWarningDanger',
      }),
    ).unwrap();

    if (!confirmed) {
      return false;
    }
  }

  dispatch(closeTab(index));
  // get roles content from server
  if (tab.path === ROLES_TAB_PATH && tab.flags.touched) {
    const fetchRoles = async () => {
      const info = (await getNodeInfo({
        verb: DesignerRequestVerb.GET_INFO,
        path: ROLES_TAB_PATH,
        content: true,
        as: 'json',
      })) as INodeInfo;
      dispatch(
        setRolesContent({
          content: info.content,
          date: info.date,
        }),
      );
    };
    fetchRoles();
  }
  return true;
});

export const tryCloseOtherTabs = createAsyncThunk<
  void,
  string,
  { state: AppState }
>('files/tryCloseOtherTabs', async (path, { getState, dispatch }) => {
  const { tabs } = getState().root;
  const someNotSaved = tabs.some(
    (curr) => curr.path !== path && curr.flags.touched === true,
  );

  if (someNotSaved) {
    const { confirmed } = await dispatch(
      openModal({
        title: 'Closing other tabs',
        message:
          'Your changes will be lost if you do not save them. Close anyway?',
        type: ModalType.CONFIRM,
        color: ModalColor.WARNING,
        icon: 'FdWarningDanger',
      }),
    ).unwrap();

    if (!confirmed) {
      return;
    }
  }

  dispatch(closeOthers(path));
});

export const tryCloseAllTabs = createAsyncThunk<
  void,
  void,
  { state: AppState }
>('files/tryCloseAllTabs', async (_, { getState, dispatch }) => {
  const { tabs } = getState().root;
  const someNotSaved = tabs.some((tab) => tab.flags.touched === true);

  if (someNotSaved) {
    const { confirmed } = await dispatch(
      openModal({
        title: 'Closing all tabs',
        message:
          'Your changes will be lost if you do not save them. Close anyway?',
        type: ModalType.CONFIRM,
        color: ModalColor.WARNING,
        icon: 'FdWarningDanger',
      }),
    ).unwrap();

    if (!confirmed) {
      return;
    }
  }

  dispatch(closeAllTabs());
});

export const tryCloseRightTabs = createAsyncThunk<
  void,
  number,
  { state: AppState }
>('files/tryCloseRightTabs', async (index, { getState, dispatch }) => {
  const { tabs } = getState().root;
  let touched = false;
  for (let i = tabs.length - 1; i > index; i -= 1) {
    if (tabs[i].flags.touched) {
      touched = true;
      break;
    }
  }

  if (touched) {
    const { confirmed } = await dispatch(
      openModal({
        title: 'Closing right tabs',
        message:
          'Your changes will be lost if you do not save them. Close anyway?',
        type: ModalType.CONFIRM,
        color: ModalColor.WARNING,
        icon: 'FdWarningDanger',
      }),
    ).unwrap();

    if (!confirmed) {
      return;
    }
  }

  dispatch(closeRightTabs(index));
});

export const tryCloseLeftTabs = createAsyncThunk<
  void,
  number,
  { state: AppState }
>('files/tryCloseLeftTabs', async (index, { getState, dispatch }) => {
  const { tabs } = getState().root;
  let touched = false;
  for (let i = 0; i < index; i += 1) {
    if (tabs[i].flags.touched) {
      touched = true;
      break;
    }
  }

  if (touched) {
    const { confirmed } = await dispatch(
      openModal({
        title: 'Closing left tabs',
        message:
          'Your changes will be lost if you do not save them. Close anyway?',
        type: ModalType.CONFIRM,
        color: ModalColor.WARNING,
        icon: 'FdWarningDanger',
      }),
    ).unwrap();

    if (!confirmed) {
      return;
    }
  }

  dispatch(closeLeftTabs(index));
});

export const reloadTab = createAsyncThunk<
  INodeInfo | void,
  ITab,
  { state: AppState }
>('files/reloadTab', async (tab, { dispatch }) => {
  if (tab.flags.touched) {
    const { confirmed } = await dispatch(
      openModal({
        title: getModalTitle(tab),
        message: 'Changes made to the file will be lost. Continue?',
        type: ModalType.CONFIRM,
        color: ModalColor.WARNING,
        icon: 'FdWarningDanger',
      }),
    ).unwrap();

    if (!confirmed) {
      throw new Error('Cancel modal!');
    }
  }

  const ext = getExtension(tab.path);

  const response = (await getNodeInfo({
    verb: DesignerRequestVerb.GET_INFO,
    path: tab.path,
    content: true,
    as: ext,
  })) as INodeInfo;

  if (tab.path === ROLES_TAB_PATH) {
    const { roles, privileges } = sanitize(response.content);

    dispatch(updateRolesAndPrivileges({ roles, privileges }));
  } else if (tab.path === SETTINGS_TAB_PATH) {
    const { content } = response;

    dispatch(setSettings({ path: 'project', value: content }));
  } else {
    const { content } = response;

    if (tab.type === FileFolderType.WEBFORM) {
      webformSubject.next({
        path: tab.path,
        action: WebformActions.RELOAD,
        payload: content.components,
      });
    } else if (tab.type === FileFolderType.MODEL) {
      modelSubject.next({
        action: 'reload',
        payload: content,
      });
    }
  }

  return response;
});

type SaveTabReturnType = ITab & {
  errors: IMultilineMessage[];
};

export const saveTab = createAsyncThunk<
  SaveTabReturnType | null,
  {
    tab: ITab;
  },
  {
    state: AppState;
  }
>('files/saveTab', async ({ tab }, { dispatch, getState }) => {
  let { path, content } = tab;

  if (tab.type === FileFolderType.DEBUGGER) return { ...tab, errors: [] };

  const chunks = path.split('/');

  chunks.pop();

  const fileInfo = await getFilesInfo({
    path,
  });

  if (fileInfo.files[0]) {
    const oldModDate = new Date(tab.date);
    const newModDate = new Date(fileInfo.files[0].date);

    if (oldModDate.getTime() < newModDate.getTime()) {
      const { confirmed } = await dispatch(
        openModal({
          title: getModalTitle(tab),
          message:
            'New content has been found on the server. Are you sure you want to continue?',
          type: ModalType.CONFIRM,
          icon: 'FdSave',
        }),
      ).unwrap();

      if (!confirmed) {
        dispatch(
          setTabFlags({
            path,
            flags: {
              old: true,
            },
          }),
        );
        return null;
      }
    }
  }

  const parsedContent = getParsedContent(tab, getState());
  const info = await saveContent({
    verb: DesignerRequestVerb.SAVE_CONTENT,
    content: parsedContent,
    path,
  });

  if (tab.type === FileFolderType.CLASS || tab.type === FileFolderType.MODEL) {
    setTimeout(() => {
      dispatch(fetchCatalog());
    }, CATALOG_REFRESH_DELAY);
  }

  // there's a bug in saveContent server side it always returns the ms section of the mod date as '000Z'
  // so I use Get File info after save to get all the portions right
  const modelErrorMessages = (info.modelErrors || []).map((err) => err.message);
  const permissionsErrorMessages = (info.permissionsErrors || []).map(
    (err) => err.message,
  );

  if (FileFolderType.MODEL === tab.type) {
    content = parsedContent;
  }

  return {
    ...tab,
    content,
    date: info.date,
    initialContent: content,
    errors:
      modelErrorMessages.length > 0 || permissionsErrorMessages.length > 0
        ? [
            { title: 'Model errors', messages: modelErrorMessages },
            { title: 'Permissions errors', messages: permissionsErrorMessages },
          ]
        : [],
  };
});

const getParsedContent = (tab: ITab, state: AppState) => {
  switch (true) {
    case tab.path === 'settings.json':
    case tab.path === SETTINGS_TAB_PATH:
      return state.settings.project;

    // case tab.type === FileFolderType.METHOD:
    //   return tab.extra?.attributes
    //     ? addAttributesToContent(tab.content, tab.extra!.attributes!, true)
    //     : tab.content;

    case tab.type === FileFolderType.ROLES: {
      if (tab.extra?.editor?.key === 'editors:roles') {
        return toAPI(state.roles);
      }
      return tab.content;
    }

    case tab.type === FileFolderType.MODEL: {
      const content = tab.content || {
        toJSON: true,
        dataClasses: [],
        extraProperties: {},
      };

      return addUUIDToModel(content);
    }

    default:
      return tab.content;
  }
};

const getFileTab = async (file: IFileInfo) => {
  if (file.type === FileFolderType.DATA_EXPLORER) {
    const tab: ITab = {
      name: 'Data Explorer',
      path: file.path,
      flags: {},
      view: {
        panel: {
          isOpen: false,
          type: '',
          current: '',
        },
      },
      type: file.type,
      date: new Date().toISOString(),
    };
    return tab;
  }

  // The below getFile api is outdated, replacing it with the getNodeInfo api fixes the issue docs#2874
  // const root = file.path.split('/').filter(Boolean)[0] as FolderRootType;
  // const response = await getFile({
  //   root,
  //   type: file.type as FileFolderTypeWithContent,
  //   verb: DesignerRequestVerb.GET,
  //   name: file.name,
  // });

  const ext = getExtension(file.path);
  const response = (await getNodeInfo({
    verb: DesignerRequestVerb.GET_INFO,
    path: file.path,
    content: true,
    as: ext,
  })) as INodeInfo;

  const extra: ITabExtra = {};
  if (response.type === FileFolderType.METHOD) {
    extra.attributes = parseAttributes(response.content);
    //response.content = removeAttributesString(response.content);
  }

  const { date, name, type, content } = response;
  const tab: ITab = {
    date,
    flags: { enabled: file.type === FileFolderType.WEBFORM },
    extra,
    view: {
      panel: {
        isOpen: false,
        type: '',
        current: '',
      },
    },
    name,
    path: file.path,
    type,
    initialContent: content,
    content:
      type === FileFolderType.MODEL ? deserializeModel(content) : content,
    history: {
      stack: [],
      active: -1,
      locked: false,
    },
  };

  return tab;
};

const debouncedSetHistory = debounce((path: string, dispatch: Dispatch) => {
  dispatch(setHistoryContent(path));
}, 300);

export const setTabContent = createAsyncThunk<
  void,
  { path: string; content: any; contentPath?: string },
  { state: AppState }
>('tabs/setTabContent', (payload, { getState, dispatch }) => {
  const {
    root: { tabs },
  } = getState();

  const tab = tabs.find((item) => item.path === payload.path);
  const isLocked = !!tab?.history?.locked;

  dispatch(setContent(payload));

  if (!isLocked) {
    debouncedSetHistory(payload.path, dispatch);
  } else {
    dispatch(unlockHistory(payload.path));
  }
});

export const setCurrentWebformState = createAsyncThunk<
  void,
  { currentState: WebformState; path: string },
  { state: AppState }
>('tabs/setCurrentWebformState', (payload, { getState, dispatch }) => {
  const {
    root: { tabs },
  } = getState();

  const tab = tabs.find((item) => item.path === payload.path);
  const isLocked = !!tab?.history?.locked;

  dispatch(setWebformState(payload));

  if (!isLocked) {
    debouncedSetHistory(payload.path, dispatch);
  } else {
    dispatch(unlockHistory(payload.path));
  }
});

export const tryInjectMethod = createAsyncThunk<
  ITab | null,
  { name: string; type?: ClassFileTypes; dataclass?: string; expand?: boolean },
  { state: AppState }
>(
  'files/tryInjectMethod',
  async ({ type, dataclass, expand }, { getState, dispatch }) => {
    const {
      root: { tabs },
      settings: {
        server: { qodly },
      },
    } = getState();
    const ext = getFileExtentionFromType('class', qodly);
    let fileName = '';
    let filePath = '';
    let tab: OrNull<ITab> = null;

    if (!dataclass) {
      fileName = 'DataStore';
      filePath = `Classes/DataStore.${ext}`;
    } else {
      switch (type) {
        case 'dataclass':
          fileName = dataclass;
          filePath = `Classes/${dataclass}.${ext}`;
          break;
        case 'collection':
          fileName = `${dataclass}Selection`;
          filePath = `Classes/${dataclass}Selection.${ext}`;
          break;
        case 'entity':
          fileName = `${dataclass}Entity`;
          filePath = `Classes/${dataclass}Entity.${ext}`;
          break;
        default:
          return tab;
      }
    }

    const found = tabs.find(by('path', filePath));

    if (found) {
      dispatch(selectTabByReference(found));
      return found;
    }

    try {
      tab = await getFileTab({
        name: fileName,
        date: new Date().toISOString(),
        path: filePath,
        type: FileFolderType.CLASS,
      });
    } catch (err) {
      if (dataclass) {
        await dispatch(
          createModelFiles({
            type: 'dataclass',
            className: dataclass,
            collectionName: `${dataclass}Selection`,
            toCreate: type,
            expand,
          }),
        );
      } else {
        // datastore case
        await dispatch(
          createModelFiles({
            type: 'model',
          }),
        );
      }
    }

    tab = await getFileTab({
      name: fileName,
      date: new Date().toISOString(),
      path: filePath,
      type: FileFolderType.CLASS,
    });

    tab.path = filePath;

    dispatch(selectTabByReference(tab));
    return tab;
  },
);

export const openRoles = createAsyncThunk<
  ITab | void,
  {},
  {
    state: AppState;
    rejectValue: string;
  }
>('roles/open', async (_payload, { dispatch, getState }) => {
  const { tabs } = getState().root;
  const found = tabs.find((tab) => tab.path === ROLES_TAB_PATH);

  if (found) {
    dispatch(selectTabByReference(found));
    return found;
  }

  dispatch(fetchRoles());
});
